import { useTheme } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';

/**
 * Jetons du thème Manewco, en deux jeux aux clés identiques : clair (maquette « Clarté complète »,
 * variante contrastée, sur la charte manewco.com #014247 / #069EAA) et sombre (dérivé de la
 * même charte, contrastes WCAG AA vérifiés par `manewcoTheme.test.ts` côté app).
 *
 * Ils vivent dans le socle et non dans `packages/app` : les plugins doivent les lire sans
 * importer l'application. Le thème les dépose dans sa palette (`palette.manewco`) et
 * `useManewcoTokens()` / `manewcoTokens(theme)` les y relisent, si bien que tout composant suit
 * le thème actif (clair ou sombre), y compris après un choix fait dans « Paramètres ».
 *
 * C'est l'un des deux seuls endroits (avec `manewcoTheme.ts`) où une couleur s'écrit en dur :
 * le test `noHardcodedColors.test.ts` (app) refuse toute couleur hexadécimale dans un `.tsx`.
 */
export interface ManewcoTokens {
  mode: 'light' | 'dark';
  primary: string;
  primaryHover: string;
  /** Texte posé sur `primary` (bouton principal, pastilles numérotées). */
  onPrimary: string;
  secondary: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  line: string;
  /** Bordure des champs : ≥ 3:1 sur la surface et sur le fond de page (WCAG 1.4.11). */
  fieldLine: string;
  pale: string;
  hover: string;
  tableHead: string;
  tableHeadText: string;
  tableRowOdd: string;
  tableRowHover: string;
  navBackground: string;
  navSubmenu: string;
  navText: string;
  navHeading: string;
  outline: string;
  /** Champ « Rechercher » du menu. */
  searchField: string;
  /** Pastilles discrètes du menu (type d'entité, compteur). */
  badge: string;
  /** Fond de l'étiquette `StatusPill` neutre. */
  neutralPill: string;
  /** Fond des extraits de code en ligne. */
  code: string;
  /** Icônes d'aide discrètes (ⓘ). */
  muted: string;
  shadow: string;
  popoverShadow: string;
  /** Étiquettes : texte « main » sur fond « light » (≥ 4,5:1). */
  tones: Record<'success' | 'warning' | 'error' | 'info', { main: string; light: string }>;
  /** Texte posé sur un ton plein (« main ») : pastilles d'état des sauvegardes. */
  onTone: string;
  /** Fond des avertissements d'erreur (éditeur SQL). */
  errorSoft: string;
  /** Couleurs vives posées en fond plein sous un texte `onVivid` : identiques dans les deux modes. */
  vivid: {
    green: string;
    red: string;
    amber: string;
    blue: string;
    blueDeep: string;
    slate: string;
  };
  onVivid: string;
  /**
   * Les mêmes teintes employées comme texte, icône, pastille ou bordure sur la surface ou sur
   * un fond `soft` (éclaircies en sombre).
   */
  vividText: { green: string; red: string; amber: string; blue: string; purple: string };
  /** Fonds pâles des toasts. */
  soft: { green: string; red: string; amber: string; blue: string };
  toastText: string;
  /** Bandeau d'accueil du tableau de bord : aplat de marque, identique dans les deux modes. */
  hero: {
    background: string;
    text: string;
    eyebrow: string;
    body: string;
    button: string;
    buttonText: string;
    buttonHover: string;
    ring: string;
    art: string;
  };
  /** Consoles (journaux, terminal, traces) : sombres dans les deux modes, par convention. */
  console: {
    background: string;
    text: string;
    header: string;
    headerText: string;
    link: string;
    selection: string;
    key: string;
    value: string;
    muted: string;
    /** Journal d'exécution du scaffolder. */
    deepBackground: string;
    deepText: string;
  };
  /** Production : sélecteur et trait rouges, texte blanc (6,6:1) dans les deux modes. */
  production: { background: string; text: string };
  radiusCard: number;
  radius: number;
  fontFamily: string;
}

const FONT = 'Arial, Helvetica, sans-serif';

/** Valeurs de la maquette, inchangées : le rendu clair reste identique au pixel près. */
export const MANEWCO_LIGHT: ManewcoTokens = {
  mode: 'light',
  primary: '#014247',
  primaryHover: '#07565B',
  onPrimary: '#FFFFFF',
  secondary: '#069EAA',
  background: '#E9F0F0',
  surface: '#FFFFFF',
  text: '#0D2C30',
  textSecondary: '#455C60',
  line: '#CBD9DA',
  fieldLine: '#778B8E',
  pale: '#D9EEF0',
  hover: '#E9F2F2',
  tableHead: '#EEF4F4',
  tableHeadText: '#33494D',
  tableRowOdd: '#F7FAFA',
  tableRowHover: '#F0F7F7',
  navBackground: '#FFFFFF',
  navSubmenu: '#F3F8F8',
  navText: '#344E52',
  navHeading: '#506A6E',
  outline: '#B9CBCD',
  searchField: '#F1F5F5',
  badge: '#F0F4F4',
  neutralPill: '#F1F4F4',
  code: '#E4EDED',
  muted: '#9e9e9e',
  shadow: '0 1px 2px #01424714, 0 6px 18px #01424710',
  popoverShadow: '0 8px 24px rgba(1, 66, 71, 0.18)',
  tones: {
    success: { main: '#23704E', light: '#E8F4EE' },
    warning: { main: '#885412', light: '#FFF2DC' },
    error: { main: '#AC302C', light: '#FBEAE8' },
    info: { main: '#0A5F80', light: '#E2F1F7' },
  },
  onTone: '#FFFFFF',
  errorSoft: '#FDECEA',
  vivid: {
    green: '#16A34A',
    red: '#DC2626',
    amber: '#F59E0B',
    blue: '#2563EB',
    blueDeep: '#1e40af',
    slate: '#6B7280',
  },
  onVivid: '#FFFFFF',
  vividText: { green: '#16A34A', red: '#DC2626', amber: '#F59E0B', blue: '#2563EB', purple: '#8B5CF6' },
  soft: { green: '#F0FDF4', red: '#FEF2F2', amber: '#FFFBEB', blue: '#EFF6FF' },
  toastText: '#4B5563',
  hero: {
    background: '#014247',
    text: '#FFFFFF',
    eyebrow: '#BFE1E3',
    body: '#D4E9EA',
    button: '#FFFFFF',
    buttonText: '#014247',
    buttonHover: '#E9F2F2',
    ring: '#7FB3B788',
    art: '#87D9DC',
  },
  console: {
    background: '#1e1e1e',
    text: '#d4d4d4',
    header: '#111',
    headerText: '#fff',
    link: '#90caf9',
    selection: '#264f78',
    key: '#9cdcfe',
    value: '#6a9955',
    muted: '#6a6a6a',
    deepBackground: '#0A0A0A',
    deepText: '#E2E8F0',
  },
  production: { background: '#B71C1C', text: '#FFFFFF' },
  radiusCard: 16,
  radius: 9,
  fontFamily: FONT,
};

/**
 * Mode sombre : fonds sarcelle très sombres tirés de #014247, accent cyan éclairci depuis #069EAA
 * pour rester lisible en texte (liens, entrée active). Les tons d'étiquettes s'inversent (texte
 * clair sur fond foncé du même ton). Bandeau d'accueil et production gardent les valeurs du mode
 * clair (ils portent leur propre fond) ; consoles et couleurs vives aussi, sauf les teintes
 * corrigées pour le contraste AA.
 */
export const MANEWCO_DARK: ManewcoTokens = {
  ...MANEWCO_LIGHT,
  mode: 'dark',
  primary: '#5CC8D1',
  primaryHover: '#7DD6DD',
  onPrimary: '#062A2D',
  secondary: '#2BB5C0',
  background: '#0B1D20',
  surface: '#132A2E',
  text: '#E4EFF0',
  textSecondary: '#A9C0C3',
  line: '#2A4549',
  fieldLine: '#6F8D91',
  pale: '#1A4247',
  hover: '#1A3337',
  tableHead: '#183236',
  tableHeadText: '#B9CED1',
  tableRowOdd: '#163034',
  tableRowHover: '#1B3A3E',
  navBackground: '#0F2427',
  navSubmenu: '#132B2F',
  navText: '#C2D5D7',
  navHeading: '#8FAAAE',
  outline: '#45666A',
  searchField: '#173034',
  badge: '#1D383C',
  neutralPill: '#1D383C',
  code: '#1D383C',
  muted: '#7F989B',
  shadow: '0 1px 2px #00000066, 0 6px 18px #0000004D',
  popoverShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
  tones: {
    success: { main: '#6FD3A0', light: '#123628' },
    warning: { main: '#F0B566', light: '#3A2A12' },
    error: { main: '#FF8F86', light: '#3E1A19' },
    info: { main: '#72C6EC', light: '#0F2E3D' },
  },
  onTone: '#0B1D20',
  errorSoft: '#3E1A19',
  // Fonds pleins sous un texte blanc : vert et ambre assombris pour atteindre 5:1 (les teintes
  // claires de la maquette restent en mode clair, voir les exceptions du test de contraste).
  vivid: { ...MANEWCO_LIGHT.vivid, green: '#15803D', amber: '#B45309' },
  vividText: { green: '#4ADE80', red: '#F87171', amber: '#FBBF24', blue: '#7AA7FF', purple: '#B39DFF' },
  soft: { green: '#123628', red: '#3E1A19', amber: '#3A2A12', blue: '#14294A' },
  toastText: '#C6D3D5',
  // Consoles : seul le texte discret change (3,08:1 → 4,83:1 sur le fond de console).
  console: { ...MANEWCO_LIGHT.console, muted: '#8A8A8A' },
};

/** Jetons du thème donné : ceux déposés par le thème Manewco, sinon le jeu du même mode. */
export const manewcoTokens = (theme: { palette: { type?: string } }): ManewcoTokens =>
  (theme.palette as { manewco?: ManewcoTokens }).manewco ??
  (theme.palette.type === 'dark' ? MANEWCO_DARK : MANEWCO_LIGHT);

/** Jetons du thème actif (clair ou sombre), pour les composants. */
export const useManewcoTokens = (): ManewcoTokens => manewcoTokens(useTheme());

/** Couleur d'environnement par défaut quand `platform.environments[].color` est absent. */
export const DEFAULT_ENV_COLOR = '#1976d2';

/**
 * Couleur d'environnement employée comme TEXTE. Les couleurs de `platform.environments` sont
 * foncées (texte sur fond clair, ≥ 4,5:1) : en mode sombre on les éclaircit de moitié pour
 * qu'elles restent lisibles sur la surface foncée. En fond plein (sélecteur, trait de la
 * production) on garde la couleur d'origine, sous un texte blanc.
 */
export const envTextColor = (theme: { palette: { type?: string } }, color: string): string =>
  theme.palette.type === 'dark' ? lighten(color, 0.5) : color;
